import { pgTable, text, boolean, timestamp, integer, index, foreignKey, jsonb, numeric, uniqueIndex, serial, varchar } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import {
  orderTargetType,
  paymentStatus,
  paymentProvider,
  accessTargetType,
  accessSourceType,
  accessStatus,
  reviewTargetType,
  contentStatus,
} from './enums';
import { user } from './user';
import { file } from './files';
import { adminUser } from './admin';
import { pricingTier } from './pricing-tier';
import { chatRoom } from './messaging';

export const order = pgTable('Order', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  statusConfigId: integer().notNull(),
  assignedAdminId: integer(),
  total: numeric({ precision: 12, scale: 2 }).notNull(),
  discountTotal: numeric({ precision: 12, scale: 2 }).default('0').notNull(),
  currency: text().default('USD').notNull(),
  deletedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
  chatRoomId: integer(),
  promoCodeId: integer(),
}, (table) => [
  index('Order_assignedAdminId_idx').using('btree', table.assignedAdminId.asc().nullsLast()),
  index('Order_chatRoomId_idx').using('btree', table.chatRoomId.asc().nullsLast()),
  index('Order_deletedAt_idx').using('btree', table.deletedAt.desc().nullsLast()),
  index('Order_statusConfigId_idx').using('btree', table.statusConfigId.asc().nullsLast()),
  index('Order_userId_idx').using('btree', table.userId.asc().nullsLast()),
  // Partial composite index for orders-cleanup.cron.ts: находит брошенные
  // preview-заказы (statusConfigId=initial, createdAt < cutoff) среди
  // неудалённых записей. См. миграцию 0117_order_preview_cleanup_idx.sql.
  index('Order_statusConfigId_createdAt_preview_idx')
    .using('btree', table.statusConfigId.asc().nullsLast(), table.createdAt.asc().nullsLast())
    .where(sql`"deletedAt" IS NULL`),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'Order_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.statusConfigId],
    foreignColumns: [orderStatusConfig.id],
    name: 'Order_statusConfigId_fkey',
  }).onUpdate('cascade').onDelete('restrict'),
  foreignKey({
    columns: [table.assignedAdminId],
    foreignColumns: [adminUser.id],
    name: 'Order_assignedAdminId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.chatRoomId],
    foreignColumns: [chatRoom.id],
    name: 'Order_chatRoomId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

export const orderItem = pgTable('OrderItem', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  orderId: integer().notNull(),
  targetType: orderTargetType().notNull(),
  targetId: integer().notNull(),
  unitPrice: numeric({ precision: 12, scale: 2 }).notNull(),
  quantity: integer().default(1).notNull(),
  total: numeric({ precision: 12, scale: 2 }).notNull(),
  pricingTierId: integer(),
  metadata: jsonb(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('OrderItem_orderId_idx').using('btree', table.orderId.asc().nullsLast()),
  index('OrderItem_targetType_idx').using('btree', table.targetType.asc().nullsLast()),
  index('OrderItem_targetId_idx').using('btree', table.targetId.asc().nullsLast()),
  foreignKey({
    columns: [table.orderId],
    foreignColumns: [order.id],
    name: 'OrderItem_orderId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.pricingTierId],
    foreignColumns: [pricingTier.id],
    name: 'OrderItem_pricingTierId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

export const payment = pgTable('Payment', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  orderId: integer().notNull(),
  userId: integer().notNull(),
  amount: numeric({ precision: 12, scale: 2 }).notNull(),
  currency: text().notNull(),
  status: paymentStatus().default('PENDING').notNull(),
  provider: paymentProvider().default('MANUAL').notNull(),
  providerPaymentId: text(),
  providerPayload: jsonb(),
  proofUrl: text(),
  proofFileId: integer(),
  paidAt: timestamp({ precision: 3, mode: 'date' }),
  orderAmount: numeric({ precision: 12, scale: 2 }),
  orderCurrency: text(),
  providerExchangeRate: numeric({ precision: 18, scale: 8 }),
  providerExchangeRateSource: text(),
  providerExchangeRateFetchedAt: timestamp({ precision: 3, mode: 'date' }),
  fixedExchangeRate: numeric({ precision: 18, scale: 8 }),
  fixedBaseCurrency: text(),
  fixedAmountBase: numeric({ precision: 12, scale: 2 }),
  // Аналитика: денормализованный snapshot тарифа на момент покупки.
  // tierIdAtPurchase — id PricingTier из orderItem; nullable, т.к. tier может
  // быть удалён, и для legacy-платежей без orderItem.pricingTierId.
  // Не FK — снимок остаётся даже после удаления тарифа.
  tierIdAtPurchase: integer(),
  // discountAmountBase — фактическая скидка в базовой валюте (если применялся
  // промокод/promoPrices). Заполняется при confirm. По умолчанию 0.
  refundedAmount: numeric('refunded_amount', { precision: 10, scale: 2 }),
  discountAmountBase: numeric({ precision: 14, scale: 2 }).default('0').notNull(),
  // originalAmountBase — оригинальная цена без скидки в базовой валюте.
  // Nullable: заполняется только если discountAmountBase > 0.
  originalAmountBase: numeric({ precision: 14, scale: 2 }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('Payment_createdAt_idx').using('btree', table.createdAt.asc().nullsLast()),
  index('Payment_orderId_idx').using('btree', table.orderId.asc().nullsLast()),
  index('Payment_status_idx').using('btree', table.status.asc().nullsLast()),
  index('Payment_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('Payment_tierIdAtPurchase_idx').using('btree', table.tierIdAtPurchase.asc().nullsLast()),
  foreignKey({
    columns: [table.orderId],
    foreignColumns: [order.id],
    name: 'Payment_orderId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'Payment_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.proofFileId],
    foreignColumns: [file.id],
    name: 'Payment_proofFileId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

export const accessGrant = pgTable('AccessGrant', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  targetType: accessTargetType().notNull(),
  targetId: integer(),
  sourceType: accessSourceType().notNull(),
  sourceId: integer(),
  sourceSubscriptionMemberId: integer(),
  sourceChannelPassId: integer(),
  orderId: integer(),
  startsAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  endsAt: timestamp({ precision: 3, mode: 'date' }),
  status: accessStatus().default('ACTIVE').notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('AccessGrant_endsAt_idx').using('btree', table.endsAt.asc().nullsLast()),
  index('AccessGrant_status_idx').using('btree', table.status.asc().nullsLast()),
  index('AccessGrant_sourceSubscriptionMemberId_idx').using('btree', table.sourceSubscriptionMemberId.asc().nullsLast()),
  index('AccessGrant_sourceChannelPassId_idx').using('btree', table.sourceChannelPassId.asc().nullsLast()),
  index('AccessGrant_orderId_idx').using('btree', table.orderId.asc().nullsLast()),
  index('AccessGrant_userId_targetType_targetId_idx').using('btree', table.userId.asc().nullsLast(), table.targetType.asc().nullsLast(), table.targetId.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'AccessGrant_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.orderId],
    foreignColumns: [order.id],
    name: 'AccessGrant_orderId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

export const review = pgTable('Review', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  orderId: integer(),
  targetType: reviewTargetType().notNull(),
  targetId: integer().notNull(),
  rating: integer().notNull(),
  content: text(),
  status: contentStatus().default('PENDING_MODERATION').notNull(),
  rejectionReason: text(),
  likesCount: integer().default(0).notNull(),
  publishedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('Review_targetType_targetId_userId_key').using('btree', table.targetType.asc(), table.targetId.asc(), table.userId.asc()),
  index('Review_targetType_targetId_idx').using('btree', table.targetType.asc(), table.targetId.asc()),
  index('Review_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('Review_status_idx').using('btree', table.status.asc().nullsLast()),
  index('Review_rating_idx').using('btree', table.rating.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'Review_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.orderId],
    foreignColumns: [order.id],
    name: 'Review_orderId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

export const reviewImage = pgTable('ReviewImage', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  reviewId: integer().notNull(),
  fileId: integer().notNull(),
  order: integer().default(0).notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('ReviewImage_reviewId_idx').using('btree', table.reviewId.asc().nullsLast()),
  foreignKey({
    columns: [table.reviewId],
    foreignColumns: [review.id],
    name: 'ReviewImage_reviewId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.fileId],
    foreignColumns: [file.id],
    name: 'ReviewImage_fileId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

export const reviewLike = pgTable('ReviewLike', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  reviewId: integer().notNull(),
  userId: integer().notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  uniqueIndex('ReviewLike_reviewId_userId_key').using('btree', table.reviewId.asc(), table.userId.asc()),
  index('ReviewLike_reviewId_idx').using('btree', table.reviewId.asc().nullsLast()),
  foreignKey({
    columns: [table.reviewId],
    foreignColumns: [review.id],
    name: 'ReviewLike_reviewId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'ReviewLike_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

export const ratingAggregate = pgTable('RatingAggregate', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  targetType: reviewTargetType().notNull(),
  targetId: integer().notNull(),
  averageRating: integer().default(0).notNull(),
  totalReviews: integer().default(0).notNull(),
  rating1Count: integer().default(0).notNull(),
  rating2Count: integer().default(0).notNull(),
  rating3Count: integer().default(0).notNull(),
  rating4Count: integer().default(0).notNull(),
  rating5Count: integer().default(0).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('RatingAggregate_targetType_targetId_key').using('btree', table.targetType.asc(), table.targetId.asc()),
]);

export const orderStatusConfig = pgTable('OrderStatusConfig', {
  id: serial().primaryKey().notNull(),
  name: text().notNull(),
  description: text(),
  color: text().default('#6b7280'),
  position: integer().notNull(),
  isSystem: boolean().default(false),
  isInitial: boolean().default(false),
  isFinal: boolean().default(false),
  onEnterActions: jsonb().default([]),
  onExitActions: jsonb().default([]),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('OrderStatusConfig_position_idx').using('btree', table.position.asc().nullsLast()),
  index('OrderStatusConfig_isSystem_idx').using('btree', table.isSystem.asc().nullsLast()),
]);

export const orderStatusTransition = pgTable('OrderStatusTransition', {
  id: serial().primaryKey().notNull(),
  fromStatusId: integer(),
  toStatusId: integer().notNull(),
  requireNote: boolean().default(false),
  requireConfirmation: boolean().default(false),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  uniqueIndex('OrderStatusTransition_fromTo_key').using('btree', table.fromStatusId.asc().nullsLast(), table.toStatusId.asc().nullsLast()),
  index('OrderStatusTransition_fromStatusId_idx').using('btree', table.fromStatusId.asc().nullsLast()),
  index('OrderStatusTransition_toStatusId_idx').using('btree', table.toStatusId.asc().nullsLast()),
  foreignKey({
    columns: [table.fromStatusId],
    foreignColumns: [orderStatusConfig.id],
    name: 'OrderStatusTransition_fromStatusId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.toStatusId],
    foreignColumns: [orderStatusConfig.id],
    name: 'OrderStatusTransition_toStatusId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

export const orderHistory = pgTable('OrderHistory', {
  id: serial().primaryKey().notNull(),
  orderId: integer().notNull(),
  adminUserId: integer(),
  action: text().notNull(),
  previousValue: jsonb(),
  newValue: jsonb(),
  fromStatusId: integer(),
  toStatusId: integer(),
  // Источник/инициатор смены статуса: 'admin' | 'admin_crm' | 'user_manual_request'
  // | 'payment_webhook' | 'system_trigger' | 'reconciliation' | 'system'.
  // NULL — историческая запись до введения поля.
  source: text(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('OrderHistory_orderId_idx').using('btree', table.orderId.asc().nullsLast()),
  index('OrderHistory_adminUserId_idx').using('btree', table.adminUserId.asc().nullsLast()),
  index('OrderHistory_createdAt_idx').using('btree', table.createdAt.desc().nullsLast()),
  index('OrderHistory_action_idx').using('btree', table.action.asc().nullsLast()),
  foreignKey({
    columns: [table.orderId],
    foreignColumns: [order.id],
    name: 'OrderHistory_orderId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'OrderHistory_adminUserId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.fromStatusId],
    foreignColumns: [orderStatusConfig.id],
    name: 'OrderHistory_fromStatusId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
  foreignKey({
    columns: [table.toStatusId],
    foreignColumns: [orderStatusConfig.id],
    name: 'OrderHistory_toStatusId_fkey',
  }).onUpdate('cascade').onDelete('set null'),
]);

export const orderNote = pgTable('OrderNote', {
  id: serial().primaryKey().notNull(),
  orderId: integer().notNull(),
  adminUserId: integer().notNull(),
  content: text().notNull(),
  isPinned: boolean().default(false),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('OrderNote_orderId_idx').using('btree', table.orderId.asc().nullsLast()),
  index('OrderNote_adminUserId_idx').using('btree', table.adminUserId.asc().nullsLast()),
  index('OrderNote_isPinned_idx').using('btree', table.isPinned.asc().nullsLast()),
  foreignKey({
    columns: [table.orderId],
    foreignColumns: [order.id],
    name: 'OrderNote_orderId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.adminUserId],
    foreignColumns: [adminUser.id],
    name: 'OrderNote_adminUserId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);

// user_card — хранилище токенов карт по всем эквайрингам.
// provider — какой эквайринг держит данный токен (MULTICARD, CLOUDPAYMENTS, ...).
// accountId — используется CloudPayments (строка AccountId для tokens.charge
// при MIT-сценарии recurring). Для Multicard значение не задаётся.
// Unique ограничение — compound (userId, provider, cardToken): один user может
// иметь один и тот же токен только у одного провайдера, но разные провайдеры
// могут случайно выдать совпадающие токены.
export const userCard = pgTable('user_card', {
  id: serial('id').primaryKey(),
  userId: integer('user_id').notNull(),
  provider: paymentProvider('provider').notNull().default('MULTICARD'),
  cardToken: varchar('card_token', { length: 255 }).notNull(),
  cardPan: varchar('card_pan', { length: 20 }).notNull(),
  paymentService: varchar('payment_service', { length: 50 }),
  holderName: varchar('holder_name', { length: 100 }),
  accountId: varchar('account_id', { length: 100 }),
  isDefault: boolean('is_default').notNull().default(false),
  createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
}, (table) => [
  index('user_card_userId_idx').using('btree', table.userId.asc().nullsLast()),
  uniqueIndex('user_card_userId_provider_cardToken_key').using('btree', table.userId, table.provider, table.cardToken),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'user_card_userId_fkey',
  }).onUpdate('cascade').onDelete('cascade'),
]);
